import React, { useState, useRef } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { Product, Store } from '../services/orderLinkAPI';
import ProductAttributeSelector, { ProductAttributeSelectorRef } from './ui/ProductAttributeSelector';
import PizzaPartSelectorModal, { PizzaPartData } from './PizzaPartSelectorModal';

interface ProductAttributesModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  store?: Store;
  availableProducts: Product[];
  onAdd: (
    product: Product,
    quantity: number,
    attributes: Record<string, any>,
    finalPrice: number
  ) => void;
}

const ProductAttributesModal: React.FC<ProductAttributesModalProps> = ({
  isOpen,
  onClose,
  product,
  store,
  availableProducts,
  onAdd,
}) => {
  const [quantity, setQuantity] = useState(1);
  const attributeSelectorRef = useRef<ProductAttributeSelectorRef>(null);
  const [showPartSelectorModal, setShowPartSelectorModal] = useState(false);
  const [selectedParts, setSelectedParts] = useState<PizzaPartData[]>([]);
  const [currentPartIndex, setCurrentPartIndex] = useState(0);
  const [totalParts, setTotalParts] = useState(1);
  const [selectedCorte, setSelectedCorte] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentGroupId, setCurrentGroupId] = useState<string>('');

  if (!isOpen) return null;

  // Helper function to calculate required parts based on cut type
  const getRequiredParts = (corte: string): number => {
    switch (corte) {
      case 'Inteira': return 1;
      case 'Meia': return 2;
      case 'Terço': return 3;
      case 'Quarto': return 4;
      default: return 1;
    }
  };

  const handleAdd = () => {
    if (attributeSelectorRef.current) {
      const { attributes, finalPrice } = attributeSelectorRef.current.getSummary();

      // Check if it's a fractional pizza (not Inteira)
      const isPizza = product.product_type?.type === 'Pizza';
      const corte = attributes.Corte;
      const isFractional = corte && corte !== 'Inteira';
      const shouldRequireAdditionalParts = store?.cobrar_preco_maior && isPizza && isFractional;

      if (shouldRequireAdditionalParts) {
        // Calculate how many parts are needed
        const requiredParts = getRequiredParts(corte);

        // Generate unique group ID for this pizza
        const groupId = `pizza_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setCurrentGroupId(groupId);

        // Store first part data with groupId and setup for additional parts
        setSelectedParts([{
          product,
          attributes: {
            ...attributes,
            _groupId: groupId
          },
          finalPrice
        }]);
        setCurrentPartIndex(1); // Next part to select (0-indexed, so this is part 2)
        setTotalParts(requiredParts);
        setShowPartSelectorModal(true);
        return; // Don't close yet
      }

      // Normal flow (Inteira or cobrar_preco_maior is false)
      onAdd(product, quantity, attributes, finalPrice);
      onClose();
      setQuantity(1); // Reset quantity
    }
  };

  const handlePartComplete = (newPart: PizzaPartData) => {
    // Add the new part to the selected parts array
    const updatedParts = [...selectedParts, newPart];
    setSelectedParts(updatedParts);

    // Check if we need more parts
    const nextPartIndex = currentPartIndex + 1;

    if (nextPartIndex < totalParts) {
      // Still need more parts - update index and keep modal open
      setCurrentPartIndex(nextPartIndex);
      // Modal stays open with updated state
    } else {
      // All parts selected - calculate prices and add to cart
      // IMPORTANT: Always use quantity=1 for fractional parts
      // The parts will be merged into a single item by mergeFractionalPizzas
      if (store?.cobrar_preco_maior) {
        // When cobrar_preco_maior is true, all parts have the price of the largest pizza
        const allPrices = updatedParts.map(p => p.finalPrice);
        const maxPrice = Math.max(...allPrices);

        // Add all parts with the max price and quantity=1
        updatedParts.forEach(part => {
          onAdd(part.product, 1, part.attributes, maxPrice);
        });
      } else {
        // Each part keeps its proportional price
        updatedParts.forEach(part => {
          onAdd(part.product, 1, part.attributes, part.finalPrice);
        });
      }

      // Close all modals and reset
      setShowPartSelectorModal(false);
      setSelectedParts([]);
      setCurrentPartIndex(0);
      setTotalParts(1);
      setCurrentGroupId('');  // Reset groupId
      onClose();
      setQuantity(1);
    }
  };

  const handlePartCancel = () => {
    setShowPartSelectorModal(false);
    setSelectedParts([]);
    setCurrentPartIndex(0);
    setTotalParts(1);
    setCurrentGroupId('');  // Reset groupId
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1));

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal/Drawer */}
      <div className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-50">
        <div className="bg-white w-full sm:max-w-2xl sm:rounded-[5px] shadow-xl flex flex-col max-h-[90vh] sm:max-h-[80vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-[20px] border-b border-gray-200">
            <div className="flex-1">
              <h2 className="text-[23px] font-bold text-gray-900">
                {product.name}
              </h2>
              <p className="text-[14px] text-gray-500 mt-1">
                Preço base: R$ {product.price.toFixed(2).replace('.', ',')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-[4px] transition-colors ml-4"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Body - Scrollable */}
          <div className="flex-1 overflow-y-auto p-[20px]">
            <ProductAttributeSelector
              ref={attributeSelectorRef}
              product={product}
              mode="auto"
              useRadioForCheckboxes={true}
              useRadioForDropdowns={true}
              onAttributesChange={(attrs: Record<string, any>, price: number) => {
                setSelectedCorte(attrs.Corte || '');
              }}
              hidePriceSummary={
                store?.cobrar_preco_maior &&
                product.product_type?.type === 'Pizza' &&
                selectedCorte === 'Meia'
              }
              ignorePriceForAttributes={
                store?.cobrar_preco_maior && product.product_type?.type === 'Pizza'
                  ? ['Corte']
                  : []
              }
            />
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-[20px] bg-gray-50 space-y-4">
            {/* Quantity Selector - Hidden for fractional pizzas when cobrar_preco_maior is true */}
            {!(store?.cobrar_preco_maior && product.product_type?.type === 'Pizza' && selectedCorte && selectedCorte !== 'Inteira') && (
              <div className="flex items-center justify-between">
                <span className="text-[15px] font-medium text-gray-700">
                  Quantidade
                </span>
                <div className="flex items-center border-[1.5px] border-gray-900 rounded-md">
                  <button
                    onClick={decrementQuantity}
                    className="p-2 hover:bg-gray-100 transition-colors"
                    disabled={quantity <= 1}
                  >
                    <Minus className="w-4 h-4 text-gray-600" />
                  </button>
                  <span className="px-6 py-2 font-semibold text-gray-900 min-w-[60px] text-center text-[15px]">
                    {quantity}
                  </span>
                  <button
                    onClick={incrementQuantity}
                    className="p-2 hover:bg-gray-100 transition-colors"
                  >
                    <Plus className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            )}

            {/* Add Button */}
            <button
              onClick={handleAdd}
              className="w-full btn btn-primary text-[15px] py-3"
            >
              Adicionar ao Carrinho
            </button>
          </div>
        </div>
      </div>

      {/* Pizza Part Selector Modal */}
      {selectedParts.length > 0 && (
        <PizzaPartSelectorModal
          isOpen={showPartSelectorModal}
          selectedParts={selectedParts}
          currentPartIndex={currentPartIndex}
          totalParts={totalParts}
          availableProducts={availableProducts}
          store={store}
          onComplete={handlePartComplete}
          onCancel={handlePartCancel}
        />
      )}
    </>
  );
};

export default ProductAttributesModal;
